var url = window.theme?.backendApiUrl || `https://printworks.coi-agency.services`;
class PersonalizationModal extends HTMLElement {
  constructor() {
    super();
    this.canvas = null;
    this.textboxes = [];
    this.coverImageObj = null;
    this.coverImageEl = null;
    this.userCoverImageName = "";
    this.element = this;
    this.domDelegate = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);
    this.delegateElement = new Delegate(this.element);
    this.areaOptions = JSON.parse(this.dataset.areaOptions || "{}");
    this.rows = JSON.parse(this.dataset.rows || "{}");
    this.placeholders = JSON.parse(this.dataset.placeholders || "[]");
    this.coverPhotoPrintVariants = JSON.parse(
      this.dataset.coverPhotoPrintVariants || "[]"
    );
    this.bgImage = null;

    // Remove temporary upload tokens immediately (these should not persist)
    localStorage.removeItem("upload_token");
    //  localStorage.removeItem('upload_timestamp');

    // Listen for security token changes from other tabs
    this.setupCrossTabSync();

    // Check security token usage AFTER a brief delay to avoid race conditions
    this.cartCheckPromise = this.conditionallyRemoveSecurityToken();

    // Define a base reference size (use desktop size as reference)
    this.BASE_CANVAS_SIZE = 421;
    this.MOBILE_CANVAS_SIZE = 343;
    this.DESKTOP_CANVAS_SIZE = 421;
  }

  setupCrossTabSync() {
    // Sync security tokens across browser tabs
    window.addEventListener('storage', (e) => {
      if (e.key === 'security_token' && e.newValue) {
        // Another tab updated the security token
        this.securityToken = e.newValue;

        // Also sync timestamp if available
        if (e.storageArea.getItem('upload_timestamp')) {
          this.timestamp = e.storageArea.getItem('upload_timestamp');
        }
      }
    });
  }
  connectedCallback() {
    this.checkLoadedUppy();
    this.timeStampCheck();
    this.checkLoaded();
    this.bindControls();
    this.setCoverUploadState(Boolean(this.userCoverImageUrl));
    this.setCustomTextEnabled(this.isCustomTextEnabled());
    this.updateOrderSummaryPricing();

    // preload fonts for instant switching
    this.preloadFonts();

    // Set heights after a brief delay to ensure elements are rendered
    setTimeout(() => this.setHeaderFooterHeights(), 100);

  }

  setCoverUploadState(hasUploadedImage) {
    this.dataset.coverImageUploaded = hasUploadedImage ? "true" : "false";
    if (!hasUploadedImage) {
      const uploadedWrapper = this.querySelector(".personalized-cover__uploaded-image-wrapper");
      if (uploadedWrapper) uploadedWrapper.classList.remove("show-overlay");
    } else {
      this.clearCoverImageRequiredError();
    }
    this.updateUploadedCoverPreview();
    this.updateOrderSummaryPricing();
  }

  setCoverUploadTitleText(value, scope = "main") {
    const selector =
      scope === "all"
        ? ".personalized-cover__image-upload-title"
        : `.personalized-cover__image-upload-title[data-upload-label-scope="${scope}"]`;
    const titles = this.querySelectorAll(selector);
    titles.forEach((title) => {
      title.textContent = value;
    });
  }

  resetCoverUploadTitleText() {
    const desktopLabel = this.dataset.uploadLabelDesktop;
    const mobileLabel = this.dataset.uploadLabelMobile;
    const titles = this.querySelectorAll(".personalized-cover__image-upload-title");
    titles.forEach((title) => {
      if (title.classList.contains("not_desktop")) {
        if (mobileLabel) title.textContent = mobileLabel;
      } else if (desktopLabel) {
        title.textContent = desktopLabel;
      }
    });
  }

  updateUploadedCoverPreview() {
    const previewImage = this.querySelector(".personalized-cover__uploaded-preview-image");
    if (!previewImage) return;
    previewImage.src = this.userCoverImageUrl || "";
  }

  hasImagePersonalization() {
    return this.dataset.hasImagePersonalization === "true";
  }

  isCustomTextEnabled() {
    if (!this.hasImagePersonalization()) return true;
    const input = this.querySelector('[data-action="toggle-custom-text"]');
    return Boolean(input && input.checked);
  }

  setCustomTextEnabled(enabled) {
    const effectiveEnabled = this.hasImagePersonalization() ? enabled : true;
    this.dataset.customTextEnabled = effectiveEnabled ? "true" : "false";
    this.textboxes.forEach((tb) => {
      tb.set("visible", effectiveEnabled);
      tb.set("evented", effectiveEnabled);
    });
    if (this.canvas) this.canvas.renderAll();
    this.updateOrderSummaryPricing();
  }

  formatMoneyFromCents(cents) {
    const parsed = Number(cents);
    const safeCents = Number.isFinite(parsed) ? Math.round(parsed) : 0;
    if (typeof Shopify !== "undefined" && Shopify.formatMoney && window.theme?.moneyFormat) {
      return Shopify.formatMoney(safeCents, window.theme.moneyFormat);
    }
    return (safeCents / 100).toFixed(2);
  }

  truncateImageName(name, maxLength = 25) {
    const value = String(name || "").trim();
    if (!value) return "";
    if (value.length <= maxLength) return value;
    const lastDotIndex = value.lastIndexOf(".");
    const hasExtension = lastDotIndex > 0 && lastDotIndex < value.length - 1;
    if (!hasExtension) {
      return `${value.slice(0, Math.max(0, maxLength - 3))}...`;
    }

    const base = value.slice(0, lastDotIndex);
    const extension = value.slice(lastDotIndex + 1).toUpperCase();
    const availableBaseLength = maxLength - extension.length - 3;
    if (availableBaseLength <= 0) {
      return `...${extension}`;
    }
    return `${base.slice(0, availableBaseLength)}...${extension}`;
  }

  updateOrderSummaryPricing() {
    const hasImagePersonalization = this.dataset.hasImagePersonalization === "true";
    if (!hasImagePersonalization) return;

    const originalPrice = Number(this.dataset.priceOriginal || 0);
    const textPrice = Number(this.dataset.priceText || 0);

    const hasText = this.isCustomTextEnabled();

    const personalizationPrice = hasText ? textPrice : 0;

    const totalPrice = originalPrice + personalizationPrice;
    const personalizationPriceText = this.formatMoneyFromCents(personalizationPrice);
    const totalPriceText = this.formatMoneyFromCents(totalPrice);

    this.querySelectorAll(".order-container__personalization-price").forEach((el) => {
      el.textContent = personalizationPriceText;
    });
    this.querySelectorAll(".order-container__total-price").forEach((el) => {
      el.textContent = totalPriceText;
    });
    this.querySelectorAll(".personalization-modal__atc-price").forEach((el) => {
      el.innerHTML = `|&nbsp;${totalPriceText}`;
    });
  }

  updateCoverAreaPlaceholder() {
    const placeholder = this.querySelector(".personalization-cover__image-placeholder");
    if (!placeholder || !this.canvas) return;

    const { coverX, coverY, coverWidth, coverAspectRatio } = this.getCoverImageConfig();
    if (!coverWidth || coverWidth <= 0) {
      this.dataset.coverPlaceholderVisible = "false";
      return;
    }

    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;
    const targetWidth = (coverWidth / 100) * canvasWidth;
    const parsedRatio = this.parseAspectRatioValue(coverAspectRatio) || 1;
    const targetHeight = targetWidth / parsedRatio;

    const maxLeft = Math.max(0, canvasWidth - targetWidth);
    const maxTop = Math.max(0, canvasHeight - targetHeight);
    const clampedX = Math.min(100, Math.max(0, coverX));
    const clampedY = Math.min(100, Math.max(0, coverY));
    const left = (clampedX / 100) * maxLeft;
    const top = (clampedY / 100) * maxTop;

    placeholder.style.left = `${left}px`;
    placeholder.style.top = `${top}px`;
    placeholder.style.width = `${targetWidth}px`;
    placeholder.style.height = `${targetHeight}px`;
    this.dataset.coverPlaceholderVisible = "true";
  }

  bindControls() {
    document.addEventListener("popup:opened", (e) => {
      this.attemptResize.bind(this)(e);
      // Recalculate heights when popup opens
      setTimeout(() => this.setHeaderFooterHeights(), 100);
    });

    document.addEventListener(
      "preview:opened",
      this.openImagePreview.bind(this)
    );

    this.delegateElement.on(
      "input",
      '[data-action="update-text"]',
      this.updateCanvasText.bind(this)
    );

    this.delegateElement.on(
      "click",
      '[data-action="set-text-color"]',
      this.updateCanvasTextColor.bind(this)
    );

    this.delegateElement.on(
      "click",
      '[data-action="set-font-size"]',
      this.updateCanvasFontSize.bind(this)
    );

    this.delegateElement.on(
      "input",
      ".personalized-cover__text-row input",
      this.handleCharacterLimit.bind(this)
    );

    if (responsive.matchesBreakpoint("lap_and_up")) {
      this.delegateElement.on(
        "mouseover",
        '[data-action="show-info"]',
        this.showInfoContainer.bind(this)
      );

      this.delegateElement.on(
        "mouseout",
        '[data-action="show-info"]',
        this.showInfoContainer.bind(this)
      );
    } else {
      this.delegateElement.on(
        "click",
        '[data-action="show-info"]',
        this.showInfoContainer.bind(this)
      );
    }

    this.delegateElement.on(
      "keydown",
      'label[for*="agree--personalization"]',
      this.handleCheckboxLabelKeydown.bind(this)
    );

    this.delegateElement.on(
      "change",
      '[data-action="toggle-custom-text"]',
      this.handleCustomTextToggle.bind(this)
    );

    this.delegateElement.on(
      "click",
      '[data-action="set-font-style"]',
      this.updateCanvasFontStyle.bind(this)
    );

    this.delegateElement.on(
      "click",
      '[data-action="add-to-cart-personalized"]',
      this.addToCartPersonalized.bind(this)
    );

    this.delegateElement.on(
      "click",
      '[data-action="cover-image-dropzone"]',
      this.handleCoverDropzoneClick.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="select-cover-image"]',
      this.openCoverImageSelector.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="edit-cover-image"]',
      this.openEditCoverImagePopup.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="open-delete-cover-image"]',
      this.openDeleteCoverImagePopup.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="open-replace-cover-image"]',
      this.openReplaceCoverImagePopup.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="toggle-uploaded-overlay"]',
      this.toggleUploadedOverlay.bind(this)
    );
    this.delegateElement.on(
      "click",
      '[data-action="confirm-delete-cover-image"]',
      this.confirmDeleteCoverImage.bind(this)
    );
    this.delegateElement.on(
      "dragenter",
      '[data-action="cover-image-dropzone"]',
      this.handleCoverDropDragEnter.bind(this)
    );
    this.delegateElement.on(
      "dragover",
      '[data-action="cover-image-dropzone"]',
      this.handleCoverDropDragOver.bind(this)
    );
    this.delegateElement.on(
      "dragleave",
      '[data-action="cover-image-dropzone"]',
      this.handleCoverDropDragLeave.bind(this)
    );
    this.delegateElement.on(
      "drop",
      '[data-action="cover-image-dropzone"]',
      this.handleCoverImageDrop.bind(this)
    );

    document.addEventListener(
      "exit:personalization",
      this.resetPersonalization.bind(this)
    );
    document.addEventListener(
      "cover-image:edited",
      this.handleEditedCoverImage.bind(this)
    );
    document.addEventListener(
      "click",
      this.handleDocumentClickForUploadedOverlay.bind(this)
    );
  }

  openCoverImageSelector(event) {
    if (event) event.preventDefault();
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".jpeg,.jpg,.png,.heic,.heif,image/jpeg,image/png,image/heic,image/heif";
    input.style.position = "fixed";
    input.style.left = "-9999px";

    input.addEventListener("change", (changeEvent) => {
      const file = changeEvent.target?.files && changeEvent.target.files[0];
      if (file) {
        this.applyCoverImageFile(file);
        this.closeReplaceCoverImagePopup();
      }
      input.remove();
    });

    document.body.appendChild(input);
    input.click();
  }

  handleCoverDropzoneClick(event) {
    const dropzone = this.getCoverDropzoneFromEvent(event);
    if (!dropzone) return;

    // Ignore phantom clicks when target is outside the visible dropzone bounds.
    const rect = dropzone.getBoundingClientRect();
    const isInsideBounds =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!isInsideBounds) return;

    // Keep button behavior intact; everything else in the box opens picker.
    const targetEl =
      event.target instanceof Element ? event.target : event.target?.parentElement;
    if (targetEl?.closest('[data-action="toggle-popup"]')) return;
    if (targetEl?.closest('[data-action="select-cover-image"]')) return;
    this.openCoverImageSelector(event);
  }

  handleCustomTextToggle(event) {
    const targetEl =
      event.target instanceof Element ? event.target : event.target?.parentElement;
    const toggle = targetEl?.closest('[data-action="toggle-custom-text"]');
    this.setCustomTextEnabled(Boolean(toggle && toggle.checked));
  }

  toggleUploadedOverlay(event) {
    if (responsive.matchesBreakpoint("lap_and_up")) return;

    const targetEl =
      event.target instanceof Element ? event.target : event.target?.parentElement;
    const wrapper = targetEl?.closest(".personalized-cover__uploaded-image-wrapper");
    if (!wrapper) return;
    if (targetEl?.closest('[data-action="open-replace-cover-image"]')) return;

    event.preventDefault();
    event.stopPropagation();
    wrapper.classList.toggle("show-overlay");
  }

  openEditCoverImagePopup(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.userCoverImageUrl) return;

    const editPopup = document.getElementById("modal-edit-img");
    if (!editPopup || !window.globalPopup) return;

    editPopup.setAttribute("data-img-url", this.userCoverImageUrl);
    editPopup.dataset.name = "personalized-cover";
    window.globalPopup.openPopup(editPopup, true);

    editPopup.dispatchEvent(
      new CustomEvent("edit-img:pressed", {
        bubbles: true,
        detail: { eventName: "edit-cover-img", coverMode: true },
      })
    );
  }

  openDeleteCoverImagePopup(event) {
    event.preventDefault();
    event.stopPropagation();
    const popup = this.element.querySelector("#personalization-cover__delete-image-modal");
    if (!popup || !window.globalPopup) return;
    window.globalPopup.openPopup(popup, true);
  }

  openReplaceCoverImagePopup(event) {
    event.preventDefault();
    event.stopPropagation();
    const popup = this.element.querySelector("#personalization-cover__replace-image-modal");
    if (!popup || !window.globalPopup) return;
    window.globalPopup.openPopup(popup, true);
  }

  closeReplaceCoverImagePopup() {
    const popup = this.element.querySelector("#personalization-cover__replace-image-modal");
    if (popup && window.globalPopup && popup.getAttribute("aria-expanded") === "true") {
      window.globalPopup.closePopup(popup);
    }
  }

  confirmDeleteCoverImage(event) {
    event.preventDefault();
    const popup = this.element.querySelector("#personalization-cover__delete-image-modal");
    if (popup && window.globalPopup) {
      window.globalPopup.closePopup(popup);
    }

    if (this.userCoverImageUrl && String(this.userCoverImageUrl).startsWith("blob:")) {
      URL.revokeObjectURL(this.userCoverImageUrl);
    }
    this.userCoverImageUrl = null;
    this.userCoverImageName = "";
    this.coverImageEl = null;
    this.setCoverUploadState(false);

    this.resetCoverUploadTitleText();

    const uploadedWrapper = this.querySelector(".personalized-cover__uploaded-image-wrapper");
    if (uploadedWrapper) uploadedWrapper.classList.remove("show-overlay");

    this.addCoverImageToCanvas();
    if (this.canvas) this.canvas.renderAll();
  }

  handleEditedCoverImage(event) {
    const editedDataUrl = event.detail?.dataUrl;
    if (!editedDataUrl) return;

    if (this.userCoverImageUrl && String(this.userCoverImageUrl).startsWith("blob:")) {
      URL.revokeObjectURL(this.userCoverImageUrl);
    }

    this.userCoverImageUrl = editedDataUrl;
    if (!this.userCoverImageName) {
      this.userCoverImageName = "edited-image.png";
    }
    this.coverImageEl = null;
    this.setCoverUploadState(true);
    const uploadedWrapper = this.querySelector(".personalized-cover__uploaded-image-wrapper");
    if (uploadedWrapper) uploadedWrapper.classList.remove("show-overlay");
    this.addCoverImageToCanvas();
    if (this.canvas) this.canvas.renderAll();
  }

  handleDocumentClickForUploadedOverlay(event) {
    if (responsive.matchesBreakpoint("lap_and_up")) return;
    const targetEl =
      event.target instanceof Element ? event.target : event.target?.parentElement;
    if (targetEl?.closest(".personalized-cover__uploaded-image-wrapper")) return;
    const wrapper = this.querySelector(".personalized-cover__uploaded-image-wrapper");
    if (wrapper) wrapper.classList.remove("show-overlay");
  }

  handleCoverDropDragOver(event) {
    event.preventDefault();
    const dropzone = this.getCoverDropzoneFromEvent(event);
    if (!dropzone) return;
    if (!this.isFileDragEvent(event)) return;
    this.setCoverDropzoneDragState(dropzone, true);
  }

  handleCoverDropDragEnter(event) {
    event.preventDefault();
    const dropzone = this.getCoverDropzoneFromEvent(event);
    if (!dropzone) return;
    if (!this.isFileDragEvent(event)) return;
    const dragDepth = Number(dropzone.dataset.dragDepth || "0") + 1;
    dropzone.dataset.dragDepth = String(dragDepth);
    this.setCoverDropzoneDragState(dropzone, true);
  }

  handleCoverDropDragLeave(event) {
    event.preventDefault();
    const dropzone = this.getCoverDropzoneFromEvent(event);
    if (!dropzone) return;
    const dragDepth = Math.max(Number(dropzone.dataset.dragDepth || "1") - 1, 0);
    dropzone.dataset.dragDepth = String(dragDepth);
    if (dragDepth === 0) {
      this.setCoverDropzoneDragState(dropzone, false);
    }
  }

  handleCoverImageDrop(event) {
    event.preventDefault();
    const dropzone = this.getCoverDropzoneFromEvent(event);
    if (!dropzone) return;
    dropzone.dataset.dragDepth = "0";
    this.setCoverDropzoneDragState(dropzone, false);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      const wasApplied = this.applyCoverImageFile(file);
      if (wasApplied) this.closeReplaceCoverImagePopup();
    }
  }

  setCoverDropzoneDragState(dropzone, isActive) {
    if (!dropzone) return;
    dropzone.classList.toggle("dragover", isActive);
    dropzone.style.backgroundColor = isActive ? "#8EABC233" : "";
  }

  getCoverDropzoneFromEvent(event) {
    if (
      event.currentTarget instanceof Element &&
      event.currentTarget.matches('[data-action="cover-image-dropzone"]')
    ) {
      return event.currentTarget;
    }

    const targetEl =
      event.target instanceof Element ? event.target : event.target?.parentElement;
    return (
      targetEl?.closest('[data-action="cover-image-dropzone"]') ||
      this.querySelector('[data-action="cover-image-dropzone"]')
    );
  }

  isFileDragEvent(event) {
    const dataTransfer = event.dataTransfer;
    if (!dataTransfer) return false;

    // Chrome/Firefox often expose "Files"; Safari/macOS may expose
    // "public.file-url" or only file-kind items during drag-over.
    const rawTypes = dataTransfer.types ? Array.from(dataTransfer.types) : [];
    const normalizedTypes = rawTypes.map((type) =>
      String(type || "").toLowerCase()
    );

    if (normalizedTypes.includes("files")) return true;
    if (normalizedTypes.includes("application/x-moz-file")) return true;
    if (normalizedTypes.includes("public.file-url")) return true;

    const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];
    if (items.some((item) => item?.kind === "file")) return true;

    return false;
  }

  validateCoverImageFile(file) {
    const maxSize = 50 * 1024 * 1024;
    const mime = (file.type || "").toLowerCase();
    const name = (file.name || "").toLowerCase();
    const allowedMime = ["image/jpeg", "image/png", "image/heic", "image/heif"];
    const allowedExt = [".jpeg", ".jpg", ".png", ".heic", ".heif"];

    const hasAllowedMime = allowedMime.includes(mime);
    const hasAllowedExt = allowedExt.some((ext) => name.endsWith(ext));
    if (!hasAllowedMime && !hasAllowedExt) {
      this.showErrorPopup("Unsupported image format. Use .jpeg, .jpg, .png, .heic, .heif", true);
      return false;
    }
    if (file.size > maxSize) {
      this.showErrorPopup("Image is too large. Maximum file size is 50MB.", true);
      return false;
    }
    return true;
  }

  applyCoverImageFile(file) {
    if (!this.validateCoverImageFile(file)) return false;
    if (this.userCoverImageUrl) {
      URL.revokeObjectURL(this.userCoverImageUrl);
      this.userCoverImageUrl = null;
    }

    this.userCoverImageUrl = URL.createObjectURL(file);
    this.userCoverImageName = file.name || "";
    this.setCoverUploadState(true);
    this.setCoverUploadTitleText(file.name, "main");

    this.addCoverImageToCanvas();
    this.canvas.renderAll();
    return true;
  }

  setHeaderFooterHeights() {
    const header = this.element.querySelector('.personalized-cover__header');
    const footer = this.element.querySelector('.personalization-cover__footer');

    if (!header || !footer) {
      console.warn('Header or footer not found');
      return;
    }

    const headerHeight = header.offsetHeight;
    const footerHeight = footer.offsetHeight;
    const totalHeight = headerHeight + footerHeight;

    // Set CSS custom properties on the modal element
    this.element.style.setProperty('--header-height', `${headerHeight}px`);
    this.element.style.setProperty('--footer-height', `${footerHeight}px`);
    this.element.style.setProperty('--header-footer-height', `${totalHeight}px`);

  }

  async conditionallyRemoveSecurityToken() {
    try {
      // Get current security token from localStorage
      const localSecurityToken = localStorage.getItem("security_token");

      // Fetch the current cart data from Shopify
      // Add cache-busting to prevent stale data on browser back navigation
      const response = await fetch(`/cart.js?t=${Date.now()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache"
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      const cartData = await response.json();

      // Check if cart has the security token in attributes
      const cartSecurityToken =
        cartData.attributes && cartData.attributes.private_cart_property;
      const hasCartSecurityToken =
        cartSecurityToken &&
        cartSecurityToken !== null &&
        cartSecurityToken !== "";

      // Check if cart has personalized or photo print items
      const hasPhotoPrint = cartData.items.some(
        (item) => item.properties && item.properties._is_photo_print === "true"
      );
      const hasPersonalized = cartData.items.some((item) => {
        return item.properties && item.properties._personalized_album;
      });

      // If cart has security token but no items that need it, clear it
      if (hasCartSecurityToken && !hasPhotoPrint && !hasPersonalized) {

        await fetch(`${window.routes.localeRootUrl}/cart/update.js`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            attributes: {
              private_cart_property: null,
              photo_print_order: null,
            },
          }),
        });

        // Also clear from localStorage
        localStorage.removeItem("security_token");
        this.securityToken = null;
        return true;
      }

      if (hasCartSecurityToken) {
        // Cart has a security token
        if (!localSecurityToken) {
          // Token exists in cart but not in localStorage - recover it
          localStorage.setItem("security_token", cartSecurityToken);
          this.securityToken = cartSecurityToken; // Update instance variable
        } else if (localSecurityToken !== cartSecurityToken) {
          // Tokens don't match - cart is source of truth, update localStorage
          localStorage.setItem("security_token", cartSecurityToken);
          this.securityToken = cartSecurityToken; // Update instance variable
        } else {
          // Tokens match - all good
          console.log("Security token preserved - cart items require it");
        }
      } else {
        // No security token in cart
        if (localSecurityToken) {
          // Token exists locally but not in cart - remove it
          localStorage.removeItem("security_token");
          this.securityToken = null; // Clear instance variable
        } else {
          // No token anywhere - this is normal for new users
          console.log("No security token needed - user has no cart items");
        }
      }

      // Clean up upload tokens when cart is empty
      if (cartData.item_count === 0) {
        localStorage.removeItem("upload_timestamp");
        localStorage.removeItem("upload_token");
        this.timestamp = null;
        this.uploadToken = null;
      }

      return true; // Return success status
    } catch (error) {
      console.error("Error checking cart for security token usage:", error);
      // On error, err on the side of caution and don't remove the token
      return false; // Return error status
    }
  }

  // Use integer-based calculations to avoid cumulative floating point errors
  getScaleFactor() {
    const currentSize = Math.round(this.canvas.width);
    return currentSize / this.BASE_CANVAS_SIZE;
  }

  //Check fabric script
  checkLoaded() {
    if (this.canvas) {
      // Canvas already exists, just resize and render
      this.resizeCanvas();
      return;
    }

    if (typeof fabric !== "undefined") {
      this.initCanvas();
      // Override _wrapLine to limit lines for placeholders
      this.overrideTextboxWrapLine();
    } else {
      loadScript(window.theme.fabricScript);
      setTimeout(() => this.checkLoaded(), 100);
    }
  }

  //Check uppy script
  checkLoadedUppy() {
    var _this = this;
    if (typeof Uppy !== "undefined") {
      this.uppyInit();
    } else {
      loadScript(window.theme.uppyScript);
      setTimeout(function () {
        _this.checkLoadedUppy();
      }, 100);
    }
  }

  initCanvas() {
    const c = this.querySelector("#fabric-canvas");
    this.canvas = new fabric.Canvas(c, {
      selection: false,
      preserveObjectStacking: true,
    });

    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      this.fabricImg = new fabric.Image(img, {
        left: 0,
        top: 0,
        selectable: false,
        evented: false,
      });

      this.fabricImg.isBgImage = true;
      this.canvas.add(this.fabricImg);
      this.canvas.renderAll();

      // Call resize and setup after image loads
      this.resizeCanvas();
    };

    img.onerror = () => {
      console.error("Failed to load background image:", this.dataset.img);
    };

    img.src = this.dataset.img;
  }

  getCoverImageConfig() {
    const coverImageUrl = this.userCoverImageUrl || "";
    const coverXRaw =
      this.dataset.coverImageX ??
      this.areaOptions?.cover_image_x_coordinate ??
      this.areaOptions?.x_coordinate ??
      "0";
    const coverYRaw =
      this.dataset.coverImageY ??
      this.areaOptions?.cover_image_y_coordinate ??
      this.areaOptions?.y_coordinate ??
      "0";
    const coverWidthRaw =
      this.dataset.coverImageWidth ??
      this.areaOptions?.cover_image_width ??
      "0";
    const coverAspectRatioRaw =
      this.dataset.coverImageAspectRatio ??
      this.areaOptions?.cover_image_aspect_ratio ??
      "";

    const coverX = Number.isFinite(parseFloat(coverXRaw)) ? parseFloat(coverXRaw) : 0;
    const coverY = Number.isFinite(parseFloat(coverYRaw)) ? parseFloat(coverYRaw) : 0;
    const coverWidth = Number.isFinite(parseFloat(coverWidthRaw)) ? parseFloat(coverWidthRaw) : 0;
    const coverAspectRatio = typeof coverAspectRatioRaw === "string" ? coverAspectRatioRaw.trim() : "";

    return { coverImageUrl, coverX, coverY, coverWidth, coverAspectRatio };
  }

  parseAspectRatioValue(value) {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (raw.includes(":")) {
      const [w, h] = raw.split(":").map((v) => parseFloat(v.trim()));
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
      return w / h;
    }
    if (raw.includes("/")) {
      const [w, h] = raw.split("/").map((v) => parseFloat(v.trim()));
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
      return w / h;
    }
    const numeric = parseFloat(raw);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return numeric;
  }

  placeCoverImage(imgEl, coverX, coverY, coverWidth, coverAspectRatio) {
    if (!this.canvas) return;

    if (this.coverImageObj) {
      this.canvas.remove(this.coverImageObj);
      this.coverImageObj = null;
    }

    if (!imgEl || !coverWidth || coverWidth <= 0) return;

    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;
    const targetWidth = (coverWidth / 100) * canvasWidth;
    const parsedRatio = this.parseAspectRatioValue(coverAspectRatio);
    const naturalRatio = imgEl.naturalWidth / imgEl.naturalHeight;
    const ratioToUse = parsedRatio || naturalRatio;
    const targetHeight = targetWidth / ratioToUse;

    // Object-fit: cover behavior for the configured placement box.
    const scale = Math.max(
      targetWidth / imgEl.naturalWidth,
      targetHeight / imgEl.naturalHeight
    );
    const maxLeft = Math.max(0, canvasWidth - targetWidth);
    const maxTop = Math.max(0, canvasHeight - targetHeight);
    const clampedX = Math.min(100, Math.max(0, coverX));
    const clampedY = Math.min(100, Math.max(0, coverY));
    const left = (clampedX / 100) * maxLeft;
    const top = (clampedY / 100) * maxTop;

    const scaledWidth = imgEl.naturalWidth * scale;
    const scaledHeight = imgEl.naturalHeight * scale;
    const drawLeft = left + (targetWidth - scaledWidth) / 2;
    const drawTop = top + (targetHeight - scaledHeight) / 2;

    const clipRect = new fabric.Rect({
      left: left,
      top: top,
      width: targetWidth,
      height: targetHeight,
      absolutePositioned: true,
    });

    this.coverImageObj = new fabric.Image(imgEl, {
      left: drawLeft,
      top: drawTop,
      scaleX: scale,
      scaleY: scale,
      selectable: false,
      evented: false,
      clipPath: clipRect,
    });

    this.coverImageObj.isCoverImage = true;
    this.canvas.add(this.coverImageObj);

    // Keep stacking order: background -> cover image -> text.
    const objects = this.canvas.getObjects();
    const bgIndex = objects.findIndex((obj) => obj.isBgImage);
    if (bgIndex >= 0) {
      if (typeof this.canvas.moveObjectTo === "function") {
        this.canvas.moveObjectTo(this.coverImageObj, bgIndex + 1);
      } else if (typeof this.canvas.insertAt === "function") {
        this.canvas.remove(this.coverImageObj);
        this.canvas.insertAt(this.coverImageObj, bgIndex + 1);
      }
    }
  }

  addCoverImageToCanvas() {
    const { coverImageUrl, coverX, coverY, coverWidth, coverAspectRatio } = this.getCoverImageConfig();
    this.updateCoverAreaPlaceholder();

    if (!coverImageUrl || !coverWidth || coverWidth <= 0) {
      if (this.coverImageObj) {
        this.canvas.remove(this.coverImageObj);
        this.coverImageObj = null;
      }
      return;
    }

    if (this.coverImageEl && this.coverImageEl.src === coverImageUrl) {
      this.placeCoverImage(this.coverImageEl, coverX, coverY, coverWidth, coverAspectRatio);
      return;
    }

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      this.coverImageEl = img;
      this.placeCoverImage(img, coverX, coverY, coverWidth, coverAspectRatio);
      this.canvas.renderAll();
    };
    img.onerror = () => {
      console.error("Failed to load cover image:", coverImageUrl);
    };
    img.src = coverImageUrl;
  }

  uppyInit() {
    this.uppy = new Uppy.Uppy({
      debug: true,
      autoProceed: false,
    });

    this.uppy.use(Uppy.XHRUpload, {
      endpoint: `${url}/api/upload`,
      headers: {},
      credentials: "include",
      fieldName: "file",
    });

    this.uppy.on("files-added", (files) => {
      console.log("Files added to Uppy");
    });

    this.bindEventsUppy();
  }

  bindEventsUppy() {
    this.uppy.on("upload-success", this.handleUploadSuccess.bind(this));
  }

  async addCanvasToUppy(personalizationData) {
    if (!this.canvas) {
      console.error("Canvas not initialized");
      return null;
    }

    const isExpired = await this.timeStampCheck();
    if (isExpired) {
      // show a warning
      console.warn("Upload session expired – please refresh your token.");
      return;
    }

    // Store original textbox content and temporarily clear placeholders
    const originalTextContent = [];
    this.textboxes.forEach((tb, index) => {
      originalTextContent.push(tb.text);
      // If the textbox contains placeholder text, clear it for the canvas
      const isPlaceholder =
        this.placeholders[index] && tb.text === this.placeholders[index];
      if (isPlaceholder) {
        tb.set("text", "");
      }
    });

    // Re-render canvas without placeholders
    this.canvas.renderAll();

    // Generate high quality image with 516/646 aspect ratio
    const canvasBlob = await new Promise((resolve) => {
      // First get high quality dataURL with multiplier
      const dataURL = this.canvas.toDataURL({
        format: "jpeg",
        quality: 0.9,
        multiplier: 2,
      });

      // Create a new canvas with 516/646 aspect ratio
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");

      // Set desired dimensions (multiply by 2 for high quality)
      tempCanvas.width = 516 * 2;
      tempCanvas.height = 646 * 2;

      // Create image from dataURL
      const img = new Image();
      img.onload = () => {
        // Calculate scaling to fit the image into 516/646 while maintaining aspect ratio
        const scaleX = tempCanvas.width / img.width;
        const scaleY = tempCanvas.height / img.height;
        const scale = Math.min(scaleX, scaleY); // Use min to ensure it fits

        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;

        // Center the image
        const x = (tempCanvas.width - scaledWidth) / 2;
        const y = (tempCanvas.height - scaledHeight) / 2;

        // Draw the scaled image
        tempCtx.drawImage(img, x, y, scaledWidth, scaledHeight);

        // Convert to blob
        tempCanvas.toBlob(resolve, "image/jpeg", 0.9);
      };
      img.src = dataURL;
    });

    const color = personalizationData["Color"]?.replace(/\s+/g, "");
    const fontSize = personalizationData["Font Size"] || "20";
    const fontName = personalizationData["Font"]?.replace(/\s+/g, "");
    const fileName = `img_${color}_${fontSize}_${fontName}_1-1.jpeg`;

    const file = new File([canvasBlob], fileName, { type: "image/jpeg" });
    const variantInput = this.element.querySelector("[data-sku]");
    const sku = variantInput?.dataset.sku;
    const desiredSize = variantInput?.dataset.variantTitle || '';
    const barcode = variantInput?.dataset.barcode || '';

    this.uppy.setMeta({
      uploadToken: this.uploadToken,
      personalized: true,
      sku: sku,
      desiredSize,
      barcode,
      personalizationData: JSON.stringify(personalizationData),
    });

    this.uppy.addFile({
      name: file.name,
      type: file.type,
      data: file,
    });

    try {
      const uploadResult = await this.handleFileUpload();

      // Return the upload data for cart use
      if (
        uploadResult &&
        uploadResult.successful &&
        uploadResult.successful.length > 0
      ) {
        const uploadedFile = uploadResult.successful[0].response.body;
        return {
          imageUrl: uploadedFile.url,
          accessToken: uploadedFile.accessToken, // JWT token
          fileId: uploadedFile.fileId || uploadedFile.original,
        };
      }

      throw new Error("Upload failed - no successful uploads");
    } catch (error) {
      console.error("Upload error in addCanvasToUppy:", error);
      return null;
    }
  }

  handleUploadSuccess(file) {
    const uploadTokenInput = this.element.querySelector(
      "#custom_property_personalization"
    );

    if (uploadTokenInput) {
      uploadTokenInput.value = this.uploadToken;
    }

    const order_token = localStorage.getItem("security_token");
    const orderTokenInput = this.element.querySelector(
      "#order_token_personalization"
    );
    if (orderTokenInput) {
      orderTokenInput.value = order_token;
    }
  }

  async handleFileUpload() {
    //await apiService.regenerateTokens();



    const xhrUpload = this.uppy.getPlugin("XHRUpload");
    xhrUpload.setOptions({
      headers: {
        Authorization: `Bearer ${this.securityToken}`,
        "x-upload-timestamp": this.timestamp,
      },
      formData: true, // Important: enable form data
      meta: {
        personalized: true,
        personalizationData: this.uppy.getState().meta.personalizationData,
      },
    });

    try {
      const result = await this.uppy.upload();

      if (result.failed && result.failed.length > 0) {
        const firstError =
          result.failed[0].error?.message ||
          result.failed[0].error ||
          "Some files failed to upload.";
        // this.showErrorPopup(firstError, true);
      }
      return result;
    } catch (err) {
      const errorMessage =
        err?.message || "An unexpected error occurred during upload.";
      // this.showErrorPopup(errorMessage, true); // Catch unexpected upload errors
    }
  }

  async timeStampCheck() {
    let isTimestampExpired = true;
    const storedTimestampStr = localStorage.getItem("upload_timestamp");
    if (storedTimestampStr) {
      const storedTimestamp = parseInt(storedTimestampStr, 10);
      if (!isNaN(storedTimestamp)) {
        const currentTime = Date.now();
        const maxAge = 14 * 24 * 60 * 60 * 1000; // 2 weeks in milliseconds
        isTimestampExpired = currentTime - storedTimestamp > maxAge;
      }
    } else {
      console.log('No timestamp found in localStorage');
    }

    if (isTimestampExpired) {
      // Only remove upload-related tokens when expired
      // Keep security token unless specifically determined it's not needed
      localStorage.removeItem("upload_timestamp");
      localStorage.removeItem("upload_token");

      // Reset instance variables for upload tokens
      this.timestamp = null;
      this.uploadToken = null;
      this.tokenPromise = null;

      // Note: We deliberately don't remove security_token here
      // It should only be removed by conditionallyRemoveSecurityToken()
    }

    if (!this.tokenPromise || isTimestampExpired) {
      this.tokenPromise = this.setTokens();
    }

    await this.tokenPromise;
  }

  async setTokens() {
    try {
      // Wait for cart check to complete first to avoid race conditions
      if (this.cartCheckPromise) {
        await this.cartCheckPromise;
      }

      // Get existing tokens from localStorage (may have been updated by cart check)
      this.securityToken = localStorage.getItem("security_token");
      this.timestamp = localStorage.getItem("upload_timestamp");
      this.uploadToken = localStorage.getItem("upload_token");

      // Check if we need to fetch new tokens
      const needsSecurityToken = !this.securityToken;
      const needsUploadTokens = !this.uploadToken;
      const needsTimestamp = !this.timestamp;
      // If timestamp is missing, we need to fetch security token (which includes timestamp)
      const actuallyNeedsSecurityToken = needsSecurityToken || needsTimestamp;

      if (actuallyNeedsSecurityToken && needsUploadTokens) {
        // Need both security token and upload tokens
        const response = await apiService.getUploadToken("both");

        this.securityToken = response.securityToken;
        this.timestamp = response.timestamp || Date.now().toString();
        this.uploadToken = response.uploadToken;

        // Store all tokens
        if (this.securityToken) {
          localStorage.setItem("security_token", this.securityToken);
        }
        if (this.timestamp) {
          localStorage.setItem("upload_timestamp", this.timestamp);
        } else {
          console.error('ERROR: No timestamp to store!');
        }
        localStorage.setItem("upload_token", this.uploadToken);

        return true;
      } else if (needsUploadTokens && !actuallyNeedsSecurityToken) {
        // Only need upload tokens (security token exists and is valid)
        const response = await apiService.getUploadToken("upload");

        this.uploadToken = response.uploadToken;
        // Keep existing timestamp - don't update it when only fetching upload tokens

        // Store only upload token (timestamp stays unchanged)
        localStorage.setItem("upload_token", this.uploadToken);

        return true;
      } else if (actuallyNeedsSecurityToken && !needsUploadTokens) {

        const response = await apiService.getUploadToken("security");

        const existingSecurityToken = localStorage.getItem("security_token");
        const existingTimestamp = localStorage.getItem("upload_timestamp");

        // If security token stays the same, preserve timestamp
        if (existingSecurityToken === response.securityToken && existingTimestamp) {
          this.securityToken = response.securityToken;
          this.timestamp = existingTimestamp;
        } else {
          // Security token changed or no existing timestamp - use new timestamp
          this.securityToken = response.securityToken;
          this.timestamp = response.timestamp || Date.now().toString();
        }

        // Store only security token
        if (this.securityToken) {
          localStorage.setItem("security_token", this.securityToken);
        }

        if (this.timestamp) {
          localStorage.setItem("upload_timestamp", this.timestamp);
        }

        return true;
      } else {
        // All tokens exist
        return true;
      }
    } catch (error) {
      console.error("Error fetching tokens:", error);
      return false;
    }
  }

  async handleInvalidTokenError(error) {
    console.log('Handling invalid token error:', error);

    try {
      // Check for active upload processes first
      const hasActiveUpload = this.checkForActiveUploads();

      // Check current cart state
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      const cartData = await response.json();

      // Check if cart has items that require security tokens
      const hasPhotoPrint = cartData.items.some(
        item => item.properties && item.properties._is_photo_print === 'true'
      );
      const hasPersonalized = cartData.items.some(
        item => item.properties && item.properties._personalized_album
      );

      const cartNeedsTokens = hasPhotoPrint || hasPersonalized;

      // ALWAYS notify customer - never automatically reset tokens
      console.log('Invalid token detected - showing user notification dialog');
      this.showInvalidTokenDialog({
        cartData,
        cartNeedsTokens,
        hasActiveUpload,
        error
      });

    } catch (error) {
      console.error('Error handling invalid token:', error);
      // Even on error, show user dialog instead of automatic reset
      this.showInvalidTokenDialog({
        cartData: { item_count: 0, items: [] },
        cartNeedsTokens: false,
        hasActiveUpload: false,
        error: 'Unable to check cart status'
      });
    }
  }

  async resetAllTokens() {
    // Clear all tokens from localStorage
    localStorage.removeItem('security_token');
    localStorage.removeItem('upload_timestamp');
    localStorage.removeItem('upload_token');

    // Clear instance variables
    this.securityToken = null;
    this.timestamp = null;
    this.uploadToken = null;
    this.tokenPromise = null;

  }

  checkForActiveUploads() {
    // Check if uppy has active uploads
    if (this.uppy) {
      const state = this.uppy.getState();
      const hasFilesUploading = Object.values(state.files).some(file =>
        file.progress && file.progress.uploadStarted && !file.progress.uploadComplete
      );

      if (hasFilesUploading) {
        console.log('Active upload detected');
        return true;
      }
    }

    // Check for any ongoing upload indicators in the UI
    const uploadingElements = this.element.querySelectorAll('[aria-uploading="true"]');
    return uploadingElements.length > 0;
  }

  showInvalidTokenDialog({ cartData, cartNeedsTokens, hasActiveUpload, error }) {
    const itemCount = cartData.item_count || 0;

    let message = 'Your session has expired and needs to be refreshed.';
    let warningMessage = '';

    // Add specific warnings based on current state
    if (hasActiveUpload) {
      warningMessage += '<strong>⚠️ Warning:</strong> You have files currently uploading. Refreshing your session may interrupt this process.<br>';
    }

    if (cartNeedsTokens && itemCount > 0) {
      warningMessage += `<strong>📝 Note:</strong> Your cart contains ${itemCount} item${itemCount > 1 ? 's' : ''} that require a valid session.<br>`;
    }

    // Create dialog with appropriate options
    let dialogHtml = `
      <div class="invalid-token-dialog">
        <p>${message}</p>
        ${warningMessage ? `<div class="warning-message">${warningMessage}</div>` : ''}
        <p>How would you like to proceed?</p>
        <div class="dialog-buttons">`;

    if (cartNeedsTokens && itemCount > 0) {
      // Cart has items that need tokens
      dialogHtml += `
          <button class="btn-clear-cart" data-action="clear-cart-tokens">Clear Cart & Refresh Session</button>
          <button class="btn-checkout" data-action="proceed-checkout">Proceed to Checkout</button>`;
    } else {
      // Cart is empty or no items need tokens
      dialogHtml += `
          <button class="btn-refresh-session" data-action="refresh-session-only">Refresh Session</button>`;
    }

    dialogHtml += `
          <button class="btn-cancel" data-action="toggle-popup">Cancel</button>
        </div>
      </div>
    `;

    // Show in error popup but with custom content  
    const popup = this.element.querySelector('#modal-pp-error');
    const errMsg = popup.querySelector('.pp__error-msg');
    errMsg.innerHTML = dialogHtml;

    // Add event listeners for custom actions
    this.addInvalidTokenListeners();

    window.globalPopup.openPopup(popup, true);
  }

  addInvalidTokenListeners() {
    this.delegateElement.on('click', '[data-action="clear-cart-tokens"]', async () => {
      await this.clearCartAndResetTokens();
    });

    this.delegateElement.on('click', '[data-action="refresh-session-only"]', async () => {
      await this.refreshSessionOnly();
    });

    this.delegateElement.on('click', '[data-action="proceed-checkout"]', () => {
      window.location.href = '/cart';
    });
  }

  async refreshSessionOnly() {
    try {
      // Reset all tokens
      await this.resetAllTokens();

      // Close popup and show success
      const popup = this.element.querySelector('#modal-pp-error');
      window.globalPopup.closePopup(popup);

      this.showErrorPopup(
        'Session refreshed successfully. You can now proceed with your personalization.',
        false,
        'toggle-popup',
        'Continue'
      );

    } catch (error) {
      console.error('Error refreshing session:', error);
      this.showErrorPopup(
        'Error refreshing session. Please try refreshing the page.',
        false,
        'toggle-popup',
        'Close'
      );
    }
  }

  async clearCartAndResetTokens() {
    try {
      // Clear the cart
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      // Reset all tokens
      await this.resetAllTokens();

      // Close popup and show success
      const popup = this.element.querySelector('#modal-pp-error');
      window.globalPopup.closePopup(popup);

      this.showErrorPopup(
        'Cart cleared and session refreshed. You can now proceed with your personalization.',
        false,
        'toggle-popup',
        'Continue'
      );

    } catch (error) {
      console.error('Error clearing cart:', error);
      this.showErrorPopup(
        'Error clearing cart. Please try refreshing the page.',
        false,
        'toggle-popup',
        'Close'
      );
    }
  }


  addTextboxes() {
    const rowCount = this.rows.num_of_rows || 2;
    const selectedFontBtn = this.querySelector(
      '[data-action="set-font-size"][selected]'
    );
    const baseFont = selectedFontBtn
      ? parseInt(selectedFontBtn.dataset.fontSize)
      : 20;
    const rowGap = this.areaOptions.row_gap || 0;

    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;

    const scaleFactor = this.getScaleFactor();
    const scaledFontSize = baseFont * scaleFactor;

    const tbWidth = Math.round((this.areaOptions.text_field_width / 100) * canvasWidth * 2) / 2;

    const centerX = (this.areaOptions.x_coordinate / 100) * canvasWidth;
    const centerY = (this.areaOptions.y_coordinate / 100) * canvasHeight;

    const leftPosition = centerX - (tbWidth / 2);
    const textAlignment = this.areaOptions.text_alignment || "center";

    this.baseY = centerY;

    // Clear existing textboxes
    this.textboxes.forEach((tb) => this.canvas.remove(tb));
    this.textboxes = [];

    for (let i = 0; i < rowCount; i++) {
      const tb = new fabric.Textbox(this.placeholders[i] || "", {
        left: leftPosition,
        top: centerY,
        width: tbWidth,
        letterSpacing: 0,
        textAlign: textAlignment,
        fontFamily: "Arial",
        fontSize: scaledFontSize,
        editable: false,
        lockScalingX: true,
        lockScalingY: true,
        hasControls: false,
        selectable: false,
        breakWords: false,
        splitByGrapheme: false,
      });

      tb.baseFont = baseFont;
      tb.scaledFont = scaledFontSize;
      tb.minWidth = tbWidth;
      tb.isPlaceholder = true; // Mark as placeholder initially

      tb._getWidthOfCharSpacing = function () {
        return 0;
      };

      tb.on("text:changed", function () {
        if (this.width > tbWidth) {
          this.set("width", tbWidth);
          this.setCoords();
        }
      });

      tb.set("height", scaledFontSize);

      tb.getHeightOfLine = function () {
        return this.fontSize;
      };

      tb._getHeightOfLine = function () {
        return this.fontSize;
      };

      this.canvas.add(tb);
      this.textboxes.push(tb);

      const input = this.querySelector(`#fabric-text-${i + 1}`);
      if (input) {
        input.placeholder = this.placeholders[i] || "";
      }
    }

    // Position textboxes for initial placeholder state
    if (rowCount === 2) {
      const tb1 = this.textboxes[0];
      const tb2 = this.textboxes[1];
      const totalHeight = tb1.height + tb2.height + rowGap;
      const startY = centerY - totalHeight / 2;

      tb1.set("top", startY);
      tb2.set("top", startY + tb1.height + rowGap);

      tb1.setCoords();
      tb2.setCoords();
    } else {
      this.textboxes[0].set("top", centerY - this.textboxes[0].height / 2);
      this.textboxes[0].setCoords();
    }

    this.setCustomTextEnabled(this.isCustomTextEnabled());
    this.canvas.renderAll();
  }

  overrideTextboxWrapLine() {
    const originalWrapLine = fabric.Textbox.prototype._wrapLine;

    fabric.Textbox.prototype._wrapLine = function (ctx, text, lineIndex) {
      const lines = originalWrapLine.call(this, ctx, text, lineIndex);

      // If this textbox is marked as placeholder, only return first line
      if (this.isPlaceholder) {
        return lines.slice(0, 1);
      }

      return lines;
    };
  }

  updateTextboxPositions() {
    if (!this.canvas || this.textboxes.length === 0) return;

    const rowGap = this.areaOptions.row_gap || 0;
    const baseY = this.baseY;

    // Count textboxes with actual user text (not placeholders or empty)
    const filledTextboxes = this.textboxes.filter((tb, index) => {
      const text = tb.text || '';
      const isPlaceholder = this.placeholders[index] && text === this.placeholders[index];
      return text.trim() !== '' && !isPlaceholder;
    });

    const totalTextboxes = this.textboxes.length;

    if (filledTextboxes.length === 0) {
      // No actual text - KEEP STACKED LAYOUT FOR PLACEHOLDERS
      if (totalTextboxes === 2) {
        const tb1 = this.textboxes[0];
        const tb2 = this.textboxes[1];
        const totalHeight = tb1.height + tb2.height + rowGap;
        const startY = baseY - totalHeight / 2;

        tb1.set("top", startY);
        tb2.set("top", startY + tb1.height + rowGap);
      } else {
        this.textboxes[0].set("top", baseY - this.textboxes[0].height / 2);
      }
    } else if (filledTextboxes.length === 1 && totalTextboxes === 2) {
      // Only one row has actual text - center it vertically
      const filledTb = filledTextboxes[0];

      filledTb.set("top", baseY - filledTb.height / 2);

      // Position the empty/placeholder textbox at the same location but it won't be visible
      const emptyTb = this.textboxes.find((tb, index) => {
        const text = tb.text || '';
        const isPlaceholder = this.placeholders[index] && text === this.placeholders[index];
        return text.trim() === '' || isPlaceholder;
      });

      if (emptyTb) {
        emptyTb.set("top", baseY - emptyTb.height / 2);
      }
    } else if (filledTextboxes.length === 2) {
      // Both rows have actual text - distribute around base Y
      const tb1 = this.textboxes[0];
      const tb2 = this.textboxes[1];

      const totalHeight = tb1.height + tb2.height + rowGap;
      const startY = baseY - totalHeight / 2;

      tb1.set("top", startY);
      tb2.set("top", startY + tb1.height + rowGap);
    } else if (totalTextboxes === 1) {
      // Single textbox - center it at base Y
      const tb = this.textboxes[0];
      tb.set("top", baseY - tb.height / 2);
    }

    this.textboxes.forEach((tb) => tb.setCoords());
    this.canvas.renderAll();
  }

  // Applies text to a canvas textbox, truncating the CANVAS display to a single line
  // without touching input.value. Uses trimEnd() before measuring to prevent trailing
  // spaces from giving a false "fits" result in Fabric's word-wrap calculation.
  // maxWidth is required: Fabric's _wrapLine expands the effective wrap threshold to the
  // widest single word, so single-word overflow is NEVER detected via _textLines.length.
  // We also compare calcTextWidth() against maxWidth to catch that case.
  // Returns the number of characters the canvas can display (null = no truncation).
  syncCanvasText(tb, text, maxWidth) {
    const fitsOnOneLine = (str) => {
      // Fabric expands tb.width when a word is wider than maxWidth; without resetting
      // width before each measure, calcTextWidth() can look "in budget" and the
      // same font-size click will wrongly clear overflow errors.
      if (maxWidth != null) {
        tb.set("width", maxWidth);
        tb.minWidth = maxWidth;
      }
      tb.set("text", str.trimEnd());
      tb._clearCache();
      tb.initDimensions();
      const wraps = tb._textLines && tb._textLines.length > 1;
      const overflows = maxWidth != null && tb.calcTextWidth() > maxWidth;
      return !wraps && !overflows;
    };

    if (fitsOnOneLine(text)) {
      if (maxWidth != null) {
        tb.set("width", maxWidth);
        tb.minWidth = maxWidth;
      }
      tb.set("text", text);
      return null;
    }

    let display = text;
    while (display.length > 0) {
      display = display.slice(0, -1);
      if (fitsOnOneLine(display)) {
        const trimmed = display.trimEnd();
        if (maxWidth != null) {
          tb.set("width", maxWidth);
          tb.minWidth = maxWidth;
        }
        tb.set("text", trimmed);
        tb._clearCache();
        tb.initDimensions();
        return trimmed.length;
      }
    }

    if (maxWidth != null) {
      tb.set("width", maxWidth);
      tb.minWidth = maxWidth;
    }
    tb.set("text", "");
    tb._clearCache();
    tb.initDimensions();
    return 0;
  }

  // Method to trigger repositioning
  updateCanvasText(event) {
    const input = event.target;
    const index = parseInt(input.dataset.row) - 1;
    const tb = this.textboxes[index];

    if (tb) {
      const canvasWidth = this.canvas.width;
      const maxWidth = (this.areaOptions.text_field_width / 100) * canvasWidth;

      const centerX = (this.areaOptions.x_coordinate / 100) * canvasWidth;
      const leftPosition = centerX - (maxWidth / 2);
      const textAlignment = this.areaOptions.text_alignment || "center";

      // Mark as NOT placeholder when user types
      tb.isPlaceholder = false;

      // Clear placeholders from other rows when user starts typing
      this.textboxes.forEach((otherTb, otherIndex) => {
        if (otherIndex !== index) {
          const otherInput = this.querySelector(`#fabric-text-${otherIndex + 1}`);
          if (otherInput && otherInput.value === '') {
            const isPlaceholder = this.placeholders[otherIndex] &&
              otherTb.text === this.placeholders[otherIndex];
            if (isPlaceholder) {
              otherTb.set("text", "");
            }
          }
        }
      });

      tb.set({
        width: maxWidth,
        left: leftPosition,
        textAlign: textAlignment,
        breakWords: false,
        splitByGrapheme: false,
      });

      // Sync canvas display without touching input value.
      // Error display is handled by handleCharacterLimit which fires after this.
      this.syncCanvasText(tb, input.value, maxWidth);

      tb.setCoords();
      this.textboxes.forEach(t => t.setCoords());
      this.updateTextboxPositions();
    }
  }

  // Font/size changes
  updateCanvasFontSize(e) {
    const raw =
      e.target.nodeType === 3 ? e.target.parentElement : e.target;
    const btn = raw?.closest?.("[data-action='set-font-size']");
    if (!btn) return;

    const baseFontSize = parseInt(btn.dataset.fontSize, 10);
    if (Number.isNaN(baseFontSize)) return;

    // Clear previous selection and mark this one
    this.clearSelected("[data-action='set-font-size']");
    btn.setAttribute("selected", "");

    // Calculate scaled font size
    const scaleFactor = this.getScaleFactor();
    const scaledFontSize = baseFontSize * scaleFactor;

    // Get positioning values once to avoid recalculation issues
    const canvasWidth = this.canvas.width;
    const maxWidth = (this.areaOptions.text_field_width / 100) * canvasWidth;
    const centerX = (this.areaOptions.x_coordinate / 100) * canvasWidth;
    const leftPosition = centerX - (maxWidth / 2);
    const textAlignment = this.areaOptions.text_alignment || "center";

    // Apply font size to all textboxes WITHOUT triggering repositioning yet
    this.textboxes.forEach((tb, i) => {
      // Store both base and scaled font sizes
      tb.baseFont = baseFontSize;
      tb.scaledFont = scaledFontSize;

      // Apply all changes at once to prevent intermediate positioning issues
      tb.set({
        fontSize: scaledFontSize,
        width: maxWidth,
        left: leftPosition,
        textAlign: textAlignment,
        breakWords: false,
        splitByGrapheme: false,
        height: scaledFontSize, // Set height to match font size
      });

      // Force single line behavior
      tb.minWidth = maxWidth;

      // Sync canvas display to current input value without touching input.value.
      // If text wraps at the new font size, the canvas truncates visually while the
      // input retains the full value so the char-limit error can be shown.
      const input = this.querySelector(`#fabric-text-${i + 1}`);
      if (input) {
        this.syncCanvasText(tb, input.value, maxWidth);
        if (btn.dataset.maxCharacters) {
          const mc = parseInt(btn.dataset.maxCharacters, 10);
          if (!Number.isNaN(mc)) input.maxLength = mc;
        }
      } else {
        tb._clearCache();
        tb.initDimensions();
      }

      // Update coordinates for this textbox
      tb.setCoords();
    });

    this.handleCharacterLimit();

    // Force canvas to re-render with new dimensions
    this.canvas.renderAll();

    // only call updateTextboxPositions once at the end after all textboxes are updated
    this.updateTextboxPositions();
  }

  async updateCanvasFontStyle(event) {
    const raw =
      event.target.nodeType === 3 ? event.target.parentElement : event.target;
    const btn = raw?.closest?.('[data-action="set-font-style"]');
    if (!btn) return;

    this.clearSelected('[data-action="set-font-style"]');
    btn.setAttribute("selected", "");

    const fontFamily = btn.dataset.fontFamily;
    const fontUrl = btn.dataset.fontUrl;

    await this.loadFont(fontFamily, fontUrl);

    // Clear Fabric's per-family char-width cache so the new font's metrics are
    // measured fresh. In this Fabric build the cache lives at fabric.cache.
    fabric.cache?.clearFontCache(fontFamily);

    const canvasWidth = this.canvas.width;
    const maxWidth = (this.areaOptions.text_field_width / 100) * canvasWidth;
    const centerX = (this.areaOptions.x_coordinate / 100) * canvasWidth;
    const leftPosition = centerX - (maxWidth / 2);

    this.textboxes.forEach((tb, i) => {
      tb.set({
        fontFamily: fontFamily,
        width: maxWidth,
        left: leftPosition,
        textAlign: this.areaOptions.text_alignment || "center",
        breakWords: false,
      });
      tb.minWidth = maxWidth;

      const input = this.querySelector(`#fabric-text-${i + 1}`);
      if (input) {
        this.syncCanvasText(tb, input.value, maxWidth);
      } else {
        tb._clearCache();
        tb.initDimensions();
      }
    });

    this.handleCharacterLimit();

    this.canvas.renderAll();
    this.updateTextboxPositions();
  }

  attemptResize(retryCount = 0) {
    const maxRetries = 10; // Limit retries to prevent infinite loops

    const success = this.resizeCanvas();

    if (!success && retryCount < maxRetries) {
      // Only retry if the modal is still open and we haven't exceeded max retries
      if (this.isModalOpen()) {
        setTimeout(() => {
          this.attemptResize(retryCount + 1);
        }, 100);
      }
    } else if (!success) {
      console.error("Failed to resize canvas after", maxRetries, "attempts");
    }
  }

  isModalOpen() {
    return (
      this.element.getAttribute("aria-expanded") === "true" ||
      this.element.getAttribute("aria-hidden") === "false"
    );
  }

  resetPersonalization() {
    if (!this.canvas) return;

    // 1. Reset canvas textboxes to placeholders
    this.textboxes.forEach((tb, index) => {
      tb.set("text", this.placeholders[index] || "");
    });

    // 2. Reset all input fields
    const inputs = this.querySelectorAll(".personalized-cover__text-row input");
    inputs.forEach((input, index) => {
      input.value = "";
      input.placeholder = this.placeholders[index] || "";

      // Remove any error styling
      input.classList.remove("error");
      const inputContainer = input.parentElement;
      inputContainer.classList.remove("error");
    });

    // 3. Clear all error messages
    this.clearAllErrors();

    // 4. Reset all option selections to first options
    this.resetToFirstOptions();

    // Reset terms checkbox
    const termsCheckboxes = this.querySelectorAll('.personalization-cover__checkbox');
    termsCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    const customTextToggle = this.querySelector('[data-action="toggle-custom-text"]');
    if (customTextToggle) customTextToggle.checked = false;

    // 5. Apply the first options to canvas
    this.initDefaults();

    // 5.1 Reset uploaded cover image selection UI
    this.resetCoverUploadTitleText();
    if (this.userCoverImageUrl) {
      URL.revokeObjectURL(this.userCoverImageUrl);
      this.userCoverImageUrl = null;
    }
    this.setCustomTextEnabled(this.isCustomTextEnabled());
    this.setCoverUploadState(false);
    this.addCoverImageToCanvas();

    // 6. Re-render canvas
    this.updateTextboxPositions();
    this.canvas.renderAll();
  }

  async resetUploadToken() {
    const token = localStorage.getItem("upload_token");

    if (!token) {
      console.warn("No upload token found. Skipping deletion.");
      return;
    }

    try {
      // Attempt to delete all files associated with this token
      await apiService.deleteFiles(token, '/api/file/token-delete');

      // Clear localStorage after successful deletion
      localStorage.removeItem("upload_token");


    } catch (error) {
      console.error("Error deleting files before resetting token:", error);
    }
  }

  handleCheckboxLabelKeydown(event) {
    // Handle Enter and Space key presses
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      // Get the associated checkbox
      const label = event.target.closest("label");
      const checkboxId = label.getAttribute("for");
      const checkbox = this.element.querySelector(`#${checkboxId}`);

      if (checkbox) {
        checkbox.checked = !checkbox.checked;

        // Trigger change event if needed for any validation
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  clearAllErrors() {
    // Remove all error classes and visibility
    const errorElements = this.querySelectorAll(
      ".fabric-error, .char-limit-error, .personalization-cover__terms-error, .personalization-cover__image-error"
    );
    errorElements.forEach((error) => {
      error.classList.remove("visible");
    });

    // Clear dynamic error content
    const charLimitNumbers = this.querySelectorAll(".char-limit-number");
    charLimitNumbers.forEach((span) => span.remove());

    // Reset error containers
    const errorContainers = this.querySelectorAll(
      ".personalization-cover__footer-terms, .personalized-cover__image-upload"
    );
    errorContainers.forEach((container) => {
      container.classList.remove("error");
    });

    // Clear any dynamic char-limit-error divs
    const dynamicErrors = this.querySelectorAll(".char-limit-error");
    dynamicErrors.forEach((error) => error.remove());
  }

  clearCoverImageRequiredError() {
    const imageUploadContainer = this.querySelector(".personalized-cover__image-upload");
    const imageError = imageUploadContainer?.querySelector(".personalization-cover__image-error");
    if (imageError) imageError.classList.remove("visible");
    if (imageUploadContainer) imageUploadContainer.classList.remove("error");
  }

  showCoverImageRequiredError(btn) {
    const imageUploadContainer = this.querySelector(".personalized-cover__image-upload");
    const imageError = imageUploadContainer?.querySelector(".personalization-cover__image-error");
    if (!imageUploadContainer || !imageError) return;

    imageError.classList.add("visible");
    imageUploadContainer.classList.add("error");

    setTimeout(() => {
      imageError.classList.remove("visible");
      imageUploadContainer.classList.remove("error");
      btn?.removeAttribute("disabled");
    }, 2500);
  }

  resetToFirstOptions() {
    // Reset text color to first option
    this.clearSelected('[data-action="set-text-color"]');
    const firstColorBtn = this.querySelector('[data-action="set-text-color"]');
    if (firstColorBtn) {
      firstColorBtn.setAttribute("selected", "");

      // Update color name display
      const labelEl = this.querySelector(".text-color-name");
      if (labelEl) {
        labelEl.textContent = firstColorBtn.dataset.colorName || "";
      }
    }

    // Reset font style to first option
    this.clearSelected('[data-action="set-font-style"]');
    const firstFontBtn = this.querySelector('[data-action="set-font-style"]');
    if (firstFontBtn) {
      firstFontBtn.setAttribute("selected", "");
    }

    // Reset font size to first option
    this.clearSelected('[data-action="set-font-size"]');
    const firstSizeBtn = this.querySelector('[data-action="set-font-size"]');
    if (firstSizeBtn) {
      firstSizeBtn.setAttribute("selected", "");
    }
  }

  clearCharacterLimitErrors() {
    [
      ".fabric-input__error--smaller-font",
      ".fabric-input__error--char-limit",
      ".fabric-input__error--use-second-row",
      ".fabric-input__error--use-first-row",
      ".fabric-input__error--text-required",
    ].forEach((sel) => {
      this.querySelectorAll(sel).forEach((el) => el.classList.remove("visible"));
    });
    this.querySelectorAll(".fabric-input__error--char-limit .char-limit-number").forEach((el) =>
      el.remove()
    );
  }

  evaluateCharacterLimitForInput(input, ctx) {
    const { allInputs, fontMaxChars, smallerFontExists } = ctx;
    const rowMaxChars = parseInt(input.getAttribute("maxlength"), 10);
    const rowNum = parseInt(input.dataset.row, 10);

    const wrap = input.closest(".personalized-cover__text-input-wrapper");
    const smallerFontError = wrap?.querySelector(".fabric-input__error--smaller-font");
    const charLimitError = wrap?.querySelector(".fabric-input__error--char-limit");
    const useSecondRowError = wrap?.querySelector(".fabric-input__error--use-second-row");
    const useFirstRowError = wrap?.querySelector(".fabric-input__error--use-first-row");

    const remainingFontChars = fontMaxChars - input.value.length;
    const fontLimitReached = fontMaxChars > 0 && input.value.length >= fontMaxChars;

    const showCharLimitError = (count) => {
      let numberSpan = charLimitError?.querySelector(".char-limit-number");
      if (!numberSpan) {
        numberSpan = document.createElement("span");
        numberSpan.className = "char-limit-number";
        charLimitError?.appendChild(numberSpan);
      }
      numberSpan.textContent = ` ${count}.`;
      charLimitError?.classList.add("visible");
    };

    const showFontLimitError = () => {
      if (smallerFontExists) {
        smallerFontError?.classList.add("visible");
      } else {
        showCharLimitError(fontMaxChars);
      }
    };

    if (allInputs.length === 1) {
      if (fontLimitReached) {
        showFontLimitError();
      }
    } else {
      if (input.value.length >= rowMaxChars) {
        if (fontLimitReached) {
          showFontLimitError();
        } else if (fontMaxChars === 0 || remainingFontChars > 0) {
          const currentRowIndex = rowNum - 1;
          const otherRows = Array.from(allInputs).filter((_, i) => i !== currentRowIndex);
          const hasSpaceInOtherRows = otherRows.some(
            (inp) => inp.value.length < parseInt(inp.getAttribute("maxlength"), 10)
          );
          if (hasSpaceInOtherRows) {
            if (rowNum === 1) {
              useSecondRowError?.classList.add("visible");
            } else {
              useFirstRowError?.classList.add("visible");
            }
          }
        }
      } else if (fontLimitReached) {
        showFontLimitError();
      }
    }

    if (!charLimitError?.classList.contains("visible") && !smallerFontError?.classList.contains("visible")) {
      const rowIndex = rowNum - 1;
      const tb = this.textboxes?.[rowIndex];
      // Compare trimEnds so trailing spaces in the input don't falsely differ from canvas
      // (tb.text includes trailing spaces when they fit; input was only trimmed on one side).
      if (tb && tb.text.trimEnd() !== input.value.trimEnd()) {
        if (smallerFontExists) {
          smallerFontError?.classList.add("visible");
        } else if (charLimitError) {
          let numberSpan = charLimitError.querySelector(".char-limit-number");
          if (!numberSpan) {
            numberSpan = document.createElement("span");
            numberSpan.className = "char-limit-number";
            charLimitError.appendChild(numberSpan);
          }
          numberSpan.textContent = ` ${tb.text.length}.`;
          charLimitError.classList.add("visible");
        }
      }
    }
  }

  handleCharacterLimit(_event) {
    const selectedFontBtn = this.querySelector('[data-action="set-font-size"][selected]');
    const fontMaxChars = selectedFontBtn
      ? parseInt(selectedFontBtn.dataset.maxCharacters || "0", 10)
      : 0;

    const allInputs = this.querySelectorAll(".personalized-cover__text-row input");

    const allFontBtns = this.querySelectorAll('[data-action="set-font-size"]');
    const smallerFontExists = Array.from(allFontBtns).some(
      (btn) => parseInt(btn.dataset.maxCharacters || "0", 10) > fontMaxChars
    );

    this.clearCharacterLimitErrors();

    const ctx = { allInputs, fontMaxChars, smallerFontExists };
    allInputs.forEach((input) => {
      this.evaluateCharacterLimitForInput(input, ctx);
    });
  }

  showInfoContainer() {
    const container = document.querySelector(
      ".personalization-cover__info-container"
    );
    const isHidden = container.getAttribute("aria-hidden") === "true";
    container.setAttribute("aria-hidden", String(!isHidden));
  }

  updateCanvasTextColor(e) {
    const btn = e.target;

    this.querySelectorAll('[data-action="set-text-color"]').forEach((b) =>
      b.removeAttribute("selected")
    );
    btn.setAttribute("selected", "");

    const isSolid = btn.dataset.color !== undefined;
    const isGradient = btn.dataset.gradient !== undefined;

    this.textboxes.forEach((tb) => {
      if (isSolid) {
        tb.set("fill", btn.dataset.color);
      } else if (isGradient) {
        const gradient = new fabric.Gradient({
          type: "linear",
          coords: { x1: 0, y1: 0, x2: tb.width, y2: 0 },
          colorStops: btn.dataset.gradient.split(",").map((c, i, arr) => ({
            offset: i / (arr.length - 1),
            color: c,
          })),
        });
        tb.set("fill", gradient);
      }
    });

    const labelEl = this.querySelector(".text-color-name");
    if (labelEl) labelEl.textContent = btn.dataset.colorName || "";

    this.canvas.renderAll();
  }

  clearSelected(selector) {
    this.querySelectorAll(selector).forEach((btn) =>
      btn.removeAttribute("selected")
    );
  }

  openImagePreview() {
    let modal = this.element.querySelector(
      "#personalization-cover__preview-modal"
    );

    // Generate high quality image (2x for crisp display)
    const dataURL = this.canvas.toDataURL({
      format: "png",
      quality: 0.9,
      multiplier: 2,
    });

    const previewImg = modal.querySelector("#preview-image");
    previewImg.src = dataURL;
  }

  async loadFont(fontFamily, fontUrl) {
    if (!fontFamily || !fontUrl) return;
    const font = new FontFace(fontFamily, `url(${fontUrl})`);
    await font.load();
    document.fonts.add(font);
  }

  preloadFonts() {
    const fontBtns = this.querySelectorAll('[data-action="set-font-style"]');

    fontBtns.forEach((btn) => {
      const fontFamily = btn.dataset.fontFamily;
      const fontUrl = btn.dataset.fontUrl;

      if (fontFamily && fontUrl) {
        const fontFace = new FontFace(fontFamily, `url(${fontUrl})`);
        fontFace
          .load()
          .then((loadedFont) => {
            document.fonts.add(loadedFont);
          })
          .catch((err) => {
            console.warn(`Failed to load font ${fontFamily}:`, err);
          });
      }
    });
  }

  initDefaults() {
    const colorBtn = this.querySelector(
      "[data-action='set-text-color'][selected]"
    );
    if (colorBtn) {
      const isSolid = colorBtn.dataset.color !== undefined;
      const isGradient = colorBtn.dataset.gradient !== undefined;

      this.textboxes.forEach((tb) => {
        if (isSolid) {
          tb.set("fill", colorBtn.dataset.color);
        } else if (isGradient) {
          const gradient = new fabric.Gradient({
            type: "linear",
            coords: { x1: 0, y1: 0, x2: tb.width, y2: 0 },
            colorStops: colorBtn.dataset.gradient
              .split(",")
              .map((c, i, arr) => ({
                offset: i / (arr.length - 1),
                color: c,
              })),
          });
          tb.set("fill", gradient);
        }
      });

      const labelEl = this.querySelector(".text-color-name");
      if (labelEl) labelEl.textContent = colorBtn.dataset.colorName || "";
    }

    const sizeBtn = this.querySelector(
      "[data-action='set-font-size'][selected]"
    );
    if (sizeBtn) {
      // Use scaled font size instead of raw fontSize
      const baseFontSize = parseInt(sizeBtn.dataset.fontSize);
      const scaleFactor = this.getScaleFactor();
      const scaledFontSize = baseFontSize * scaleFactor;

      this.textboxes.forEach((tb, i) => {
        tb.set("fontSize", scaledFontSize); // Use scaled size
        const input = this.querySelector(`#fabric-text-${i + 1}`);
        if (input && sizeBtn.dataset.charLimit) {
          input.maxLength = sizeBtn.dataset.charLimit;
        }
      });
    }

    const styleBtn = this.querySelector(
      "[data-action='set-font-style'][selected]"
    );
    if (styleBtn) {
      const fontFamily = styleBtn.dataset.fontFamily;
      this.textboxes.forEach((tb) => tb.set("fontFamily", fontFamily));
    }

    this.canvas.renderAll();
  }

  resizeCanvas() {
    const container = this.querySelector(
      ".personalization-cover__image-container"
    );
    if (!container) {
      console.warn("Container not found");
      return false; // Return false to indicate failure
    }

    const containerWidth = parseFloat(
      window.getComputedStyle(container).getPropertyValue("width")
    );

    // Remove the setTimeout - just return false if width is invalid
    if (!containerWidth || containerWidth === 0) {
      console.warn("Container width is 0 - modal may not be visible");
      return false; // Return false to indicate failure
    }

    this.canvas.setWidth(containerWidth);
    this.canvas.setHeight(containerWidth);
    this.updateCoverAreaPlaceholder();

    const bgImage = this.canvas.getObjects().find((obj) => obj.isBgImage);
    if (bgImage) {
      const imgWidth = bgImage._element.naturalWidth;
      const imgHeight = bgImage._element.naturalHeight;
      const scale = Math.max(
        containerWidth / imgWidth,
        containerWidth / imgHeight
      );

      bgImage.scale(scale);
      bgImage.set({
        left: (containerWidth - bgImage.getScaledWidth()) / 2,
        top: (containerWidth - bgImage.getScaledHeight()) / 2,
      });
      bgImage.setCoords();
    }

    this.addCoverImageToCanvas();
    this.addTextboxes();
    this.initDefaults();
    this.canvas.renderAll();

    return true;
  }

  getPersonalizationData() {
    const data = {};
    const customTextEnabled = this.isCustomTextEnabled();

    if (customTextEnabled) {
      // Get text color first
      const colorBtn = this.querySelector(
        '[data-action="set-text-color"][selected]'
      );
      if (colorBtn && colorBtn.dataset.colorName) {
        data["Color"] = colorBtn.dataset.colorName;
      }

      // Get textboxes - only use actual text, not placeholders
      if (this.textboxes.length === 1) {
        // Only include text if it's not empty and not a placeholder
        const actualText = this.textboxes[0].text;
        const isPlaceholder =
          this.placeholders[0] && actualText === this.placeholders[0];
        data["Text"] = actualText && !isPlaceholder ? actualText : "";
      } else if (this.textboxes.length === 2) {
        // Check each row individually
        const text1 = this.textboxes[0].text;
        const text2 = this.textboxes[1].text;
        const isPlaceholder1 =
          this.placeholders[0] && text1 === this.placeholders[0];
        const isPlaceholder2 =
          this.placeholders[1] && text2 === this.placeholders[1];

        data["First Row"] = text1 && !isPlaceholder1 ? text1 : "";
        data["Second Row"] = text2 && !isPlaceholder2 ? text2 : "";
      }

      // Get font
      const fontBtn = this.querySelector(
        '[data-action="set-font-style"][selected]'
      );
      if (fontBtn && fontBtn.dataset.fontLabel) {
        data["Font"] = fontBtn.dataset.fontLabel;
      }

      // Get font size
      const sizeBtn = this.querySelector(
        '[data-action="set-font-size"][selected]'
      );
      if (sizeBtn && sizeBtn.dataset.sizeName) {
        data["Font Size"] = sizeBtn.dataset.sizeName;
      }
    }

    //Get placement
    const placement = this.areaOptions.placement_name;
    data["_placement"] = placement;
    return data;
  }

  async addCoverPhotoToBackend() {
    if (!this.userCoverImageUrl) return null;
    try {
      const response = await fetch(this.userCoverImageUrl);
      const blob = await response.blob();
      const filename = this.userCoverImageName || 'cover-photo.jpg';
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

      const variantInput = this.element.querySelector('[data-sku]');
      const albumVariantTitle = variantInput?.dataset.variantTitle || '';
      const coverAspectRatioKey = this.getCoverAspectRatioKey();
      const matchedCoverPrintVariant = this.getCoverPhotoPrintVariantByAspectRatio(coverAspectRatioKey);
      const hasConfiguredCoverPrintProduct = Array.isArray(this.coverPhotoPrintVariants) && this.coverPhotoPrintVariants.length > 0;
      const fallbackCoverPrintVariant = hasConfiguredCoverPrintProduct ? this.coverPhotoPrintVariants[0] : null;
      const selectedCoverPrintVariant = matchedCoverPrintVariant || fallbackCoverPrintVariant;
      const albumSkuFallback = variantInput?.dataset.sku || '';
      const coverPrintSku = selectedCoverPrintVariant?.sku || albumSkuFallback;
      const coverPrintVariantTitle = selectedCoverPrintVariant?.title || coverAspectRatioKey || albumVariantTitle || '';
      const albumBarcodeFallback = variantInput?.dataset.barcode || '';
      const coverPrintBarcode = selectedCoverPrintVariant?.barcode || albumBarcodeFallback;
      const coverAspectRatioRaw =
        this.areaOptions?.cover_image_aspect_ratio ||
        this.dataset?.coverImageAspectRatio ||
        coverPrintVariantTitle ||
        '';
      const standardizedCoverSize = this.standardizeCoverSizeValue(coverAspectRatioRaw || coverPrintVariantTitle || "");
      if (!matchedCoverPrintVariant) {
        console.warn("cover-photo-upload:variant-match-missed", {
          coverAspectRatioKey,
          hasConfiguredCoverPrintProduct,
          fallbackVariantTitle: fallbackCoverPrintVariant?.title || null,
          fallbackSku: fallbackCoverPrintVariant?.sku || null,
        });
      }
      const formData = new FormData();
      formData.append('uploadToken', this.uploadToken);
      formData.append('photoCoverPrint', 'true');
      formData.append('sku', coverPrintSku);
      formData.append('desiredSize', standardizedCoverSize || coverPrintVariantTitle);
      formData.append('barcode', coverPrintBarcode);
      formData.append('file', file, filename);
      console.info("cover-photo-upload:start", {
        uploadToken: this.uploadToken,
        filename,
        fileType: file.type,
        fileSize: file.size,
        hasSecurityToken: Boolean(this.securityToken),
        photoCoverPrint: "true",
        albumVariantTitle,
        coverAspectRatioKey,
        matchedCoverPrintVariantTitle: matchedCoverPrintVariant?.title || null,
        selectedCoverPrintVariantTitle: selectedCoverPrintVariant?.title || null,
        usedFallbackVariant: Boolean(!matchedCoverPrintVariant && fallbackCoverPrintVariant),
        coverPrintSku,
        coverPrintBarcode,
      });

      const uploadResp = await fetch(`${url}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.securityToken}`,
          'x-upload-timestamp': this.timestamp,
        },
        body: formData,
      });

      if (!uploadResp.ok) throw new Error('Cover photo upload failed');
      const responseData = await uploadResp.json();
      this.coverPhotoSkuForCart = coverPrintSku || '';
      this.coverPhotoSizeForCart = standardizedCoverSize || '';
      console.info("cover-photo-upload:success", {
        uploadToken: this.uploadToken,
        original: responseData?.original || null,
        edited: responseData?.edited || null,
        coverPhotoSkuForCart: this.coverPhotoSkuForCart || null,
        coverPhotoSizeForCart: this.coverPhotoSizeForCart || null,
        standardizedCoverSize: standardizedCoverSize || null,
      });
      return responseData;
    } catch (err) {
      console.error('Cover photo upload error:', err);
      return null;
    }
  }

  normalizeVariantTitle(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[:/]/g, 'x')
      .replace(/,/g, '.');

    // Prefer extracting an explicit size token from mixed titles, e.g.
    // "9x9 cm", "cm 9x9", "Album 10 x 15 matte" -> "9x9", "10x15"
    const dimensionMatch = normalized.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/);
    if (dimensionMatch) {
      return `${dimensionMatch[1]}x${dimensionMatch[2]}`;
    }

    return normalized.replace(/\s+/g, '');
  }

  standardizeCoverSizeValue(value) {
    return String(value || "")
      .trim()
      .replace(/[/:]+/g, "x")
      .replace(/\s*x\s*/gi, "x")
      .replace(/\s+/g, "");
  }

  getCoverAspectRatioKey() {
    const rawRatio =
      this.areaOptions?.cover_image_aspect_ratio ||
      this.dataset?.coverImageAspectRatio ||
      "";
    return this.normalizeVariantTitle(rawRatio);
  }

  getCoverPhotoPrintVariantByAspectRatio(coverAspectRatioKey) {
    if (!Array.isArray(this.coverPhotoPrintVariants) || !this.coverPhotoPrintVariants.length) return null;
    if (!coverAspectRatioKey) return null;
    const normalizedVariants = this.coverPhotoPrintVariants.map((variant) => ({
      title: variant?.title || "",
      normalizedTitle: this.normalizeVariantTitle(variant?.title),
      sku: variant?.sku || "",
      barcode: variant?.barcode || "",
    }));

    const exactMatch = this.coverPhotoPrintVariants.find((variant) => {
      return this.normalizeVariantTitle(variant?.title) === coverAspectRatioKey;
    }) || null;

    console.info("cover-photo-upload:variant-match-check", {
      coverAspectRatioKey,
      hasExactMatch: Boolean(exactMatch),
      matchedTitle: exactMatch?.title || null,
      availableVariants: normalizedVariants,
    });

    return exactMatch;
  }

  async addToCartPersonalized(event) {
    event.preventDefault();
    const btn = event.target;
    if (!btn) return;

    // if (btn.hasAttribute('disabled')) return;
    btn.setAttribute('disabled', 'true');
    const formElement = this.querySelector(
      ".personalization-modal__product-form"
    );
    if (!formElement) return;

    const customRows = this.element.querySelectorAll(
      ".personalized-cover__text-row input"
    );

    const emptyRows = [];
    const emptyInputEls = [];
    const customTextEnabled = this.isCustomTextEnabled();
    const hasCoverImageSelector = Boolean(
      this.querySelector(".personalized-cover__image-upload [data-action='cover-image-dropzone']")
    );
    const hasCoverImage = Boolean(this.userCoverImageUrl);
    if (hasCoverImageSelector && !hasCoverImage) {
      this.showCoverImageRequiredError(btn);
      return;
    }

    // Validate rows only when custom text is enabled
    if (customTextEnabled && customRows.length > 0) {
      customRows.forEach((input) => {
        if (!input.value.trim()) {
          const rowLabel = input.dataset.rowTranslate ||
            input.closest('.personalized-cover__text-row')?.querySelector('label')?.textContent?.trim() ||
            'Text field';
          emptyRows.push(rowLabel);
          emptyInputEls.push(input);
        }
      });
    }

    // Hard block: all inputs empty → show inline required errors, no popup
    if (
      emptyRows.length &&
      emptyRows.length === customRows.length &&
      !event.target.classList.contains("secondary_atc")
    ) {
      emptyInputEls.forEach((input) => {
        const requiredErr = input.closest('.personalized-cover__text-row')
          ?.querySelector('.fabric-input__error--text-required');
        if (requiredErr) requiredErr.classList.add('visible');
      });
      btn.removeAttribute('disabled');
      return;
    }

    // Block submission if a font character-limit error is currently visible
    if (customTextEnabled) {
      const smallerFontError = this.element.querySelector('.fabric-input__error--smaller-font');
      const charLimitError = this.element.querySelector('.fabric-input__error--char-limit');
      if (
        smallerFontError?.classList.contains('visible') ||
        charLimitError?.classList.contains('visible')
      ) {
        btn.removeAttribute('disabled');
        return;
      }
    }

    // Always validate terms checkbox (regardless of row count)
    let termsContainer;

    if (responsive.matchesBreakpoint("lap_and_up")) {
      termsContainer = this.element.querySelector(
        ".personalization-cover__footer-terms.not_mobile"
      );
    } else {
      termsContainer = this.element.querySelector(
        ".personalization-cover__footer-terms.not_desktop"
      );
    }

    // Check if checkbox is checked, add error if not
    const termsInput = termsContainer?.querySelector(
      'input[type="checkbox"]'
    );

    const termsError = termsContainer?.querySelector(
      ".personalization-cover__terms-error"
    );

    if (termsInput && !termsInput.checked) {
      if (termsError) {
        termsError.classList.add("visible");
        termsContainer.classList.add("error");

        setTimeout(() => {
          termsError.classList.remove("visible");
          termsContainer.classList.remove("error");
          btn.removeAttribute('disabled');
        }, 2500);
      }
      return; // Stop add-to-cart if not checked
    }

    // Soft warning: some (but not all) inputs empty → popup (terms already accepted at this point)
    if (
      emptyRows.length &&
      !event.target.classList.contains("secondary_atc")
    ) {
      let errorText = emptyRows.join(", ");

      let modal = document.getElementById(
        "personalization-cover__rows-modal"
      );
      if (modal) {
        let errorContainer = modal.querySelector(".rows-modal__empty-fields");
        if (errorContainer) {
          errorContainer.textContent = errorText;
        }
      }

      const popupToggle = this.element.querySelector(
        "#personalization-cover__rows-modal"
      );
      if (popupToggle) {
        window.globalPopup.openPopup(popupToggle, true);

        const overlayClass = popupToggle.getAttribute("data-overlay");
        if (overlayClass) {
          const overlay = this.element.querySelector(`.${overlayClass}`);
          if (overlay) {
            overlay.classList.add("is-visible");
            const handleOverlayClick = (e) => {
              e.preventDefault();
              window.globalPopup.closePopup(popupToggle);
            };
            overlay.addEventListener("click", handleOverlayClick);
            popupToggle._overlayClickHandler = handleOverlayClick;
          }
        }
      }
      btn.removeAttribute('disabled');
      return;
    }

    try {
      this.coverPhotoSkuForCart = "";
      this.coverPhotoSizeForCart = "";
      // --- Collect personalization data ---
      const personalizationData = this.getPersonalizationData();

      const uploadData = await this.addCanvasToUppy(personalizationData);

      // Upload the raw cover photo separately for PARENT_3 printing.
      // Fail fast if this upload does not succeed so backend flow can't be half-complete.
      if (this.userCoverImageUrl) {
        const coverUploadData = await this.addCoverPhotoToBackend();
        if (!coverUploadData) {
          throw new Error("Cover photo upload failed");
        }
      }

      const customProperties = {
        _upload_token: this.uploadToken || (this.element.querySelector("#custom_property_personalization") || document.getElementById("custom_property_personalization"))?.value || "",
        _order_token: (this.element.querySelector("#order_token_personalization") || document.getElementById("order_token_personalization"))?.value || "",
      };

      // Remove old personalization inputs (but keep token properties)
      formElement
        .querySelectorAll(
          'input[name^="properties["]:not([name="properties[_upload_token]"]):not([name="properties[_order_token]"])'
        )
        .forEach((i) => i.remove());

      Object.keys(personalizationData).forEach((key) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${key}]`;
        input.value = personalizationData[key];
        formElement.appendChild(input);
      });

      let attributesToSet = {};
      if (!customProperties._order_token) {
        customProperties._order_token = localStorage.getItem("security_token");
      }
      if (customProperties._order_token) {
        attributesToSet.private_cart_property = customProperties._order_token;
        attributesToSet._personalized_album = true;
      } else {
        attributesToSet.private_cart_property = null;
        attributesToSet._personalized_album = null;
      }

      // Add a marker for personalized album products
      const marker = document.createElement("input");
      marker.type = "hidden";
      marker.name = "properties[_personalized_album]";
      marker.value = "true";
      formElement.appendChild(marker);

      // Get the text personalization variant ID (first/default variant of the customization product)
      const variantInput = formElement.querySelector('[name="id"]');
      const originalProductSku = variantInput?.dataset.sku || '';
      const textVariantId = parseInt(variantInput?.value, 10);
      variantInput?.removeAttribute("disabled");

      // Serialize form for Shopify
      const formHelper = new Form();
      const serialized = formHelper.serialize(formElement);

      // Get the original product variant ID from metafield or data attribute
      const originalProductVariantId = this.getOriginalProductVariantId();

      if (!originalProductVariantId) {
        throw new Error("Original product variant ID not found");
      }

      const customTextEnabled = this.isCustomTextEnabled();

      const payloadItems = [
        {
          id: originalProductVariantId,
          quantity: parseInt(serialized.quantity, 10) || 1,
          properties: {
            "_cart_Color": serialized.properties.Color || '',
            "_cart_Font": serialized.properties.Font || '',
            "_cart_Font_Size": serialized.properties['Font Size'] || '',
            "_cart_First_Row": serialized.properties['First Row'] || '',
            "_cart_Second_Row": serialized.properties['Second Row'] || '',
            _upload_token: customProperties._upload_token,
            "_cart_Text": serialized.properties.Text || '',
            _is_original: "true",
            ...(customTextEnabled && { _customized_variant_id: String(textVariantId) }),
            _image_url: uploadData.imageUrl,
            _cover_photo_sku: this.coverPhotoSkuForCart || "",
            _cover_photo_size: this.coverPhotoSizeForCart || "",
          },
        },
      ];

      if (customTextEnabled && textVariantId) {
        payloadItems.push({
          id: textVariantId,
          quantity: parseInt(serialized.quantity, 10) || 1,
          properties: {
            ...serialized.properties,
            _hidden: true,
            _personalized: "true",
            _upload_token: customProperties._upload_token,
            _image_url: uploadData.imageUrl,
            _sku_og_product: originalProductSku,
            _cover_photo_sku: this.coverPhotoSkuForCart || "",
            _cover_photo_size: this.coverPhotoSizeForCart || "",
            _file_id: uploadData.fileId,
            _access_token: uploadData.accessToken,
          },
        });
      }

      // --- AJAX add to cart ---
      const response = await fetch(
        window.routes.localeRootUrl + "/cart/add.js",
        {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items: payloadItems,
            attributes: attributesToSet,
          }),
        }
      );

      if (!response.ok) {
        const content = await response.json();
        const errorEl =
          this.element.querySelector(".ProductForm--Error") ||
          document.querySelector(".product-error");
        if (errorEl) {
          errorEl.classList.add("visible");
          errorEl.innerHTML = content.description || "Error adding to cart";
          setTimeout(() => {
            errorEl.classList.remove("visible");
            errorEl.innerHTML = "";
          }, 3000);
        }
        btn.removeAttribute("disabled");
        return;
      }

      // --- Handle redirect or reload ---
      if (window.theme.cartMode === "page") {
        location.href = window.routes.cart_page_url;
        return;
      }

      if (window.location.href.includes("cart")) {
        location.reload();
        return;
      }

      localStorage.removeItem('upload_token');
      this.uploadToken = null;
      this.tokenPromise = null;
      btn.removeAttribute("disabled");

      this.element
        .querySelector(".exit-modal__exit-button-personalization")
        .click();

      this.element.dispatchEvent(
        new CustomEvent("product:added", {
          bubbles: true,
          detail: {
            variant: serialized.id,
            quantity: serialized.quantity,
          },
        })
      );
    } catch (err) {
      console.error("Error in addToCartPersonalized:", err);
      btn.removeAttribute("disabled");
    }
  }

  // Helper method to get the original product variant ID
  getOriginalProductVariantId() {
    const originalVariantInput = this.element.querySelector('[name="original_variant_id"]');
    if (originalVariantInput) {
      return parseInt(originalVariantInput.value, 10);
    }

    console.error("Could not find original product variant ID");
    return null;
  }

  showErrorPopup(err, closeChilds = false, action = 'upload-again-err', exitString = 'Cancel') {
    if (closeChilds == true) {
      const childsPopup = this.element.querySelectorAll('.popup-inside-popup');
      childsPopup.forEach(popup => {
        if (popup.getAttribute('aria-expanded') === 'true') {
          window.globalPopup.closePopup(popup);
        }
      });
    }
    const popup = this.element.querySelector('#modal-pp-error');
    const errMsg = popup.querySelector('.pp__error-msg');
    errMsg.textContent = err;

    const ButtonText = this.element.querySelectorAll('.pp__exit-btn .ButtonText');
    if (ButtonText) {
      ButtonText.forEach(btn => {
        btn.textContent = exitString;
      });
    }

    if (action == 'toggle-popup') {
      popup.querySelector('.pp__action-btn').style.display = 'none';
      window.globalPopup.openPopup(popup, true);
    } else {
      popup.querySelector('.pp__action-btn').style.display = 'block';
      popup.querySelector('.pp__action-btn').setAttribute('data-action', action);
      window.globalPopup.openPopup(popup, true);
    }
  }
}

customElements.define("personalization-modal", PersonalizationModal);
